/**
 * BaseCollector - Abstract base class for all data collectors
 *
 * Layer 1: Data Collection
 *
 * Purpose:
 * - Define common interface for all collectors
 * - Provide error handling and logging
 * - Ensure consistent event structure
 *
 * Responsibilities:
 * - Validate events before collection
 * - Handle collection failures gracefully (non-blocking)
 * - Log collection activity for debugging
 */

export class BaseCollector {
  /**
   * @param {Object} storageService - Layer 2 storage service
   */
  constructor(storageService) {
    if (!storageService) {
      throw new Error('BaseCollector requires a storage service');
    }
    this.storage = storageService;
    this.collectorType = this.constructor.name;
  }

  /**
   * Collect an event - must be implemented by subclasses
   * @param {Object} event - Event data to collect
   * @returns {Promise<void>}
   */
  async collect(event) {
    throw new Error(`${this.collectorType} must implement collect() method`);
  }

  /**
   * Validate event has required fields
   * @param {Object} event - Event to validate
   * @param {Array<string>} requiredFields - Required field names
   * @returns {boolean}
   */
  validateEvent(event, requiredFields) {
    if (!event) {
      console.warn(`${this.collectorType}: Received null/undefined event`);
      return false;
    }

    const missingFields = requiredFields.filter(field => !event[field]);
    if (missingFields.length > 0) {
      console.warn(`${this.collectorType}: Missing required fields:`, missingFields);
      return false;
    }

    return true;
  }

  /**
   * Safe collection wrapper with error handling
   * @param {Function} collectionFn - Collection function to execute
   * @returns {Promise<void>}
   */
  async safeCollect(collectionFn) {
    try {
      await collectionFn();
    } catch (error) {
      // Collection failures should NOT block the main operation
      // Stats are important but not critical to mention/consequence creation
      console.error(`${this.collectorType} collection failed (non-blocking):`, error);

      // Could emit to error tracking service here
      // e.g., Sentry.captureException(error, { context: this.collectorType })
    }
  }

  /**
   * Extract timestamp from various formats
   * @param {*} timestamp - Timestamp in various formats
   * @returns {Date}
   */
  normalizeTimestamp(timestamp) {
    if (!timestamp) {
      return new Date();
    }

    // Firestore Timestamp
    if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }

    // Date object
    if (timestamp instanceof Date) {
      return timestamp;
    }

    // Unix timestamp or ISO string
    return new Date(timestamp);
  }

  /**
   * Get current date key for storage (YYYY-MM-DD)
   * @param {Date} date - Date object
   * @returns {string}
   */
  getDateKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Log collection activity (can be toggled via config)
   * @param {string} message - Log message
   * @param {Object} data - Additional data to log
   */
  log(message, data = {}) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[${this.collectorType}] ${message}`, data);
    }
  }
}
